import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Search, Plus, Loader2, UserPlus, Phone, X, ArrowUpCircle, CheckCircle, Bell, Globe } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { RECIPIENT_HEX_COLORS } from './recipient-selector';
import { usePlan } from '@/contexts/PlanContext';
import { Link } from 'react-router-dom';

interface Recipient {
  id: string;
  name: string;
  email: string;
  phone?: string;
  company_name?: string;
  is_shared?: boolean;
}

interface SendTemplateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateTitle: string;
  recipientCount?: number;
  onSendComplete: () => void;
}

function stripPhone(value: string): string {
  return value.replace(/\D/g, '');
}

function formatPhoneNumber(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  if (digits.length <= 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') {
    return `1-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

function isValidNAPhone(digits: string): boolean {
  if (digits.length === 0) return true;
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits[0] === '1') return true;
  return false;
}

const SendTemplateModal: React.FC<SendTemplateModalProps> = ({
  open,
  onOpenChange,
  templateId,
  templateTitle,
  recipientCount = 1,
  onSendComplete,
}) => {
  const { t } = useTranslation(['templates', 'common', 'billing', 'documents']);
  const { toast } = useToast();
  const { canUse } = usePlan();
  const canUseSms = canUse('sms_region');
  const storedUser = localStorage.getItem('user');
  const isBusiness = storedUser ? JSON.parse(storedUser).customer_type === 'business' : false;

  const slotCount = Math.max(1, Math.min(4, recipientCount));

  const defaultLocale = (storedUser ? JSON.parse(storedUser).locale : 'en') || 'en';

  // Slot assignments: slot index (1-based) -> assigned Recipient
  const [slotAssignments, setSlotAssignments] = useState<Record<number, Recipient | null>>({});
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [searchResults, setSearchResults] = useState<Recipient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // New recipient form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [adding, setAdding] = useState(false);
  const [newLocale, setNewLocale] = useState(defaultLocale);
  const [sendSuccess, setSendSuccess] = useState(false);
  const canUseOpenNotifications = canUse('open_notifications');

  const fetchRecipients = useCallback(async (search: string) => {
    try {
      setLoading(true);
      const params = `?search=${encodeURIComponent(search)}`;
      const response = await api.get<any>(`/recipients${params}`) as any;
      const data = response?.recipients || response?.data?.recipients || [];
      setSearchResults(data);
      setHasSearched(true);
    } catch (error) {
      console.error('Error fetching recipients:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setSlotAssignments({});
      setActiveSlot(null);
      setSearchResults([]);
      setSearchTerm('');
      setDebouncedSearchTerm('');
      setShowAddForm(false);
      setHasSearched(false);
      setSendSuccess(false);
      resetAddForm();
    }
  }, [open]);

  const resetAddForm = () => {
    setNewName('');
    setNewEmail('');
    setNewPhone('');
    setNewLocale(defaultLocale);
    setPhoneError('');
    setEmailError('');
    setShowAddForm(false);
  };

  const resetSearch = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSearchResults([]);
    setHasSearched(false);
  };

  // Debounce search term
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm, open]);

  // Fetch only when debounced term has 3+ chars
  useEffect(() => {
    if (!open) return;
    if (debouncedSearchTerm.trim().length >= 3) {
      fetchRecipients(debouncedSearchTerm.trim());
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }
  }, [debouncedSearchTerm, open, fetchRecipients]);

  const openSlotSearch = (slotNum: number) => {
    setActiveSlot(slotNum);
    resetSearch();
    resetAddForm();
  };

  const assignRecipientToSlot = (recipient: Recipient) => {
    if (activeSlot === null) return;
    setSlotAssignments(prev => ({ ...prev, [activeSlot]: recipient }));
    setActiveSlot(null);
    resetSearch();
  };

  const clearSlot = (slotNum: number) => {
    setSlotAssignments(prev => {
      const next = { ...prev };
      delete next[slotNum];
      return next;
    });
  };

  // IDs already assigned to other slots (prevent duplicates)
  const assignedIds = new Set(
    Object.entries(slotAssignments)
      .filter(([slot]) => Number(slot) !== activeSlot)
      .map(([, r]) => r?.id.toString())
      .filter(Boolean)
  );

  const filledSlots = Object.values(slotAssignments).filter(Boolean).length;
  const allSlotsFilled = filledSlots === slotCount;

  const isValidEmail = (email: string): boolean => {
    if (!email) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleEmailBlur = () => {
    if (!newEmail.trim()) { setEmailError(''); return; }
    setEmailError(!isValidEmail(newEmail.trim()) ? t('templates:send_email_invalid') : '');
  };

  const handlePhoneChange = (value: string) => {
    setNewPhone(value.replace(/[^\d-]/g, ''));
    setPhoneError('');
  };

  const handlePhoneBlur = () => {
    if (!newPhone.trim()) { setPhoneError(''); return; }
    const digits = stripPhone(newPhone);
    if (isValidNAPhone(digits)) {
      setNewPhone(formatPhoneNumber(digits));
      setPhoneError('');
    } else {
      setPhoneError(t('templates:send_phone_invalid'));
    }
  };

  const handleAddRecipient = async () => {
    if (!newName.trim() || !newEmail.trim()) return;
    if (!isValidEmail(newEmail.trim())) { setEmailError(t('templates:send_email_invalid')); return; }
    const phoneDigits = stripPhone(newPhone);
    if (phoneDigits.length > 0 && !isValidNAPhone(phoneDigits)) { setPhoneError(t('templates:send_phone_invalid')); return; }

    try {
      setAdding(true);
      const payload: any = { name: newName.trim(), email: newEmail.trim(), locale: newLocale };
      if (phoneDigits.length > 0) payload.phone = formatPhoneNumber(phoneDigits);

      const response = await api.post<any>('/recipients', payload) as any;
      const newRecipient = response?.recipient || response?.data?.recipient;
      if (newRecipient) {
        assignRecipientToSlot(newRecipient);
        resetAddForm();
      }
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || 'Failed to add recipient', variant: 'destructive' });
    } finally {
      setAdding(false);
    }
  };

  const handleSend = async () => {
    if (!allSlotsFilled) return;
    try {
      setSending(true);

      // Build recipients array ordered by slot number (1, 2, 3, ...)
      const orderedRecipients = Array.from({ length: slotCount }, (_, i) => {
        const slotNum = i + 1;
        const r = slotAssignments[slotNum];
        return r ? { id: r.id, name: r.name, email: r.email, phone: r.phone || undefined } : null;
      }).filter(Boolean);

      await api.post<any>(`/templates/${templateId}/send`, {
        recipients: orderedRecipients,
        signing_workflow: slotCount > 1 ? 'parallel' : null,
      });

      setSendSuccess(true);
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.message || error.message || 'Failed to send template', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const showSearchHint = searchTerm.trim().length > 0 && searchTerm.trim().length < 3;

  // Build the slot numbers array (1-based)
  const slots = Array.from({ length: slotCount }, (_, i) => i + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{t('templates:send_title')}</DialogTitle>
          <DialogDescription>
            {slotCount > 1
              ? t('templates:send_description_multi', { title: templateTitle, count: slotCount })
              : t('templates:send_description', { title: templateTitle })}
          </DialogDescription>
        </DialogHeader>

        {sendSuccess ? (
          <div className="flex flex-col items-center py-6 gap-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">{t('templates:send_success_title', 'Document Sent!')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('templates:send_success_description', { title: templateTitle, count: filledSlots })}
              </p>
            </div>

            {!canUseOpenNotifications && (
              <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-amber-100 p-1.5 mt-0.5">
                    <Bell className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900">
                      {t('templates:send_open_notify_title', 'Want to know when they open it?')}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {t('templates:send_open_notify_description', 'Upgrade your plan to get notified when recipients open your document.')}
                    </p>
                    <Link to="/settings?tab=billing" onClick={() => { onSendComplete(); }}>
                      <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs">
                        <ArrowUpCircle className="h-3 w-3 mr-1" />
                        {t('billing:upgrade.button', 'Upgrade Plan')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={() => onSendComplete()} className="w-full">
              {t('common:actions.done', 'Done')}
            </Button>
          </div>
        ) : (
        <>
        <div className="flex-1 overflow-y-auto flex flex-col gap-3">
          {/* Recipient slots */}
          {slots.map((slotNum) => {
            const assigned = slotAssignments[slotNum] || null;
            const color = RECIPIENT_HEX_COLORS[slotNum] || RECIPIENT_HEX_COLORS[1];
            const isActive = activeSlot === slotNum;

            return (
              <div key={slotNum} className="rounded-md border p-3" style={{ borderColor: color, backgroundColor: `${color}08` }}>
                {/* Slot header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
                    <span className="text-sm font-semibold">{t('templates:editor_recipient', { number: slotNum })}</span>
                  </div>
                  {assigned && (
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => clearSlot(slotNum)}>
                      <X className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  )}
                </div>

                {/* Assigned recipient display */}
                {assigned && !isActive ? (
                  <button
                    type="button"
                    className="w-full text-left rounded-md bg-background p-2 border border-border hover:bg-accent/50 transition-colors"
                    onClick={() => openSlotSearch(slotNum)}
                  >
                    <div className="font-medium text-sm">{assigned.name}</div>
                    <div className="text-xs text-muted-foreground">{assigned.email}</div>
                    {assigned.phone && (
                      <div className="text-xs text-muted-foreground flex items-center gap-1">
                        <Phone className="h-3 w-3" />{assigned.phone}
                      </div>
                    )}
                  </button>
                ) : isActive ? (
                  /* Search UI for this slot */
                  <div className="space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder={t('templates:send_search_placeholder')}
                        className="pl-9 h-9 text-sm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        autoFocus
                      />
                    </div>

                    {showSearchHint && (
                      <p className="text-xs text-muted-foreground text-center">{t('templates:send_search_hint')}</p>
                    )}

                    {/* Search results */}
                    <div className="max-h-[160px] overflow-y-auto border rounded-md bg-background">
                      {loading ? (
                        <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin" /></div>
                      ) : !hasSearched && searchResults.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground text-xs">{t('templates:send_search_hint')}</div>
                      ) : searchResults.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground text-xs">{t('templates:send_no_recipients')}</div>
                      ) : (
                        <div className="divide-y">
                          {searchResults.map((r) => {
                            const alreadyUsed = assignedIds.has(r.id.toString());
                            return (
                              <button
                                key={r.id}
                                type="button"
                                disabled={alreadyUsed}
                                className={`w-full flex items-center gap-3 p-2.5 text-left transition-colors ${alreadyUsed ? 'opacity-40 cursor-not-allowed' : 'hover:bg-accent cursor-pointer'}`}
                                onClick={() => !alreadyUsed && assignRecipientToSlot(r)}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="font-medium text-sm truncate">{r.name}</div>
                                  <div className="text-xs text-muted-foreground truncate">{r.email}</div>
                                  {r.phone && (
                                    <div className="text-xs text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />{r.phone}</div>
                                  )}
                                </div>
                                {isBusiness && r.is_shared && <Badge variant="outline" className="text-xs shrink-0">Shared</Badge>}
                                {r.company_name && <span className="text-xs text-muted-foreground truncate max-w-[80px]">{r.company_name}</span>}
                                {alreadyUsed && <span className="text-xs text-muted-foreground italic shrink-0">{t('templates:send_already_assigned')}</span>}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Add new + cancel row */}
                    {!showAddForm ? (
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)} className="flex-1 h-8 text-xs">
                          <UserPlus className="h-3.5 w-3.5 mr-1" />{t('templates:send_add_new')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => { setActiveSlot(null); resetSearch(); }} className="h-8 text-xs">
                          {t('common:actions.cancel')}
                        </Button>
                      </div>
                    ) : (
                      <div className="border rounded-md p-2.5 space-y-2 bg-background">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">{t('templates:send_name_label')}</Label>
                            <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('templates:send_name_placeholder')} className="h-8 text-sm" />
                          </div>
                          <div>
                            <Label className="text-xs">{t('templates:send_email_label')}</Label>
                            <Input type="email" value={newEmail} onChange={(e) => { setNewEmail(e.target.value); setEmailError(''); }} onBlur={handleEmailBlur} placeholder={t('templates:send_email_placeholder')} className={`h-8 text-sm ${emailError ? 'border-red-500' : ''}`} />
                            {emailError && <p className="text-xs text-red-500 mt-0.5">{emailError}</p>}
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">{t('templates:send_phone_label')}</Label>
                            {canUseSms ? (
                              <>
                                <Input type="tel" value={newPhone} onChange={(e) => handlePhoneChange(e.target.value)} onBlur={handlePhoneBlur} placeholder={t('templates:send_phone_placeholder')} className={`h-8 text-sm ${phoneError ? 'border-red-500' : ''}`} />
                                {phoneError && <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>}
                              </>
                            ) : (
                              <div className="space-y-1">
                                <Input type="tel" disabled placeholder={t('templates:send_phone_placeholder')} className="h-8 text-sm bg-muted cursor-not-allowed" />
                                <Link to="/settings?tab=billing" className="inline-flex items-center gap-1 text-xs text-amber-600 hover:text-amber-700 font-medium">
                                  <ArrowUpCircle className="h-3 w-3" />
                                  {t('billing:upgrade.button', 'Upgrade to enable SMS')}
                                </Link>
                              </div>
                            )}
                          </div>
                          <div>
                            <Label className="text-xs">{t('documents:editor_language')}</Label>
                            <div className="flex items-center gap-2 mt-1">
                              <Globe className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                              <Select value={newLocale} onValueChange={setNewLocale}>
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="en">{t('documents:editor_language_en', 'English')}</SelectItem>
                                  <SelectItem value="fr">{t('documents:editor_language_fr', 'Français')}</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={handleAddRecipient} disabled={adding || !newName.trim() || !newEmail.trim() || !!emailError || !!phoneError} className="h-7 text-xs">
                            {adding ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Plus className="h-3 w-3 mr-1" />}
                            {adding ? t('templates:send_adding') : t('templates:send_add_button')}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={resetAddForm} className="h-7 text-xs">{t('common:actions.cancel')}</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Empty slot - click to search */
                  <button
                    type="button"
                    className="w-full border border-dashed rounded-md p-3 text-center text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
                    onClick={() => openSlotSearch(slotNum)}
                  >
                    <UserPlus className="h-4 w-4 mx-auto mb-1" />
                    {t('templates:send_assign_recipient')}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSend} disabled={sending || !allSlotsFilled}>
            {sending ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />{t('templates:send_sending')}</>
            ) : (
              t('templates:send_button', { count: filledSlots })
            )}
          </Button>
        </DialogFooter>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendTemplateModal;
